import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef } from 'react'
import { useThemeStore } from './stores/themeStore'
import { applyDirection } from './i18n/config'
import { DEFAULT_LANG, getLangFromPathname } from './utils/localeFromUrl'
import { fetchUiLabelsForLanguage } from './services/uiLabelsApiService'

// Layout
import PublicLayout from './layouts/PublicLayout'
import LangSync from './components/layout/LangSync'

// Public pages
import HomePage from './pages/public/HomePage'
import AboutPage from './pages/public/AboutPage'
import QuranPage from './pages/public/QuranPage'
import IslamDefinitionPage from './pages/public/IslamDefinitionPage'
import JourneyToIslamPage from './pages/public/JourneyToIslamPage'
import DoubtResponsesPage from './pages/public/DoubtResponsesPage'
import ServicesPage from './pages/public/ServicesPage'
import PrayerTimesPage from './pages/public/PrayerTimesPage'
import ZakatCalculatorPage from './pages/public/ZakatCalculatorPage'
import DonationsPage from './pages/public/DonationsPage'
import LibraryIndexPage from './pages/public/LibraryIndexPage'
import LibraryDetailPage from './pages/public/LibraryDetailPage'
import LibraryBookPage from './pages/public/LibraryBookPage'
import VideosPage from './pages/public/VideosPage'
import VideoDetailPage from './pages/public/VideoDetailPage'
import SectionCardArticlePage from './pages/public/SectionCardArticlePage'
import PaymentCallbackPage from './pages/public/PaymentCallbackPage'
import InstitutionsPage from './pages/public/InstitutionsPage'
import ReaderPage from './pages/public/ReaderPage'
import ReferencesPage from './pages/public/ReferencesPage'
import QuranSurahPage from './pages/public/QuranSurahPage'
import QuranListenPage from './pages/public/QuranListenPage'
import QuranNewIndexPage from './pages/public/QuranNewIndexPage'

function App() {
  const { i18n } = useTranslation()
  const i18nRef = useRef(i18n)
  i18nRef.current = i18n
  const { mode } = useThemeStore()
  const location = useLocation()

  // Merge admin UI labels (DB) into i18n. Use URL language so /ar/... gets Arabic keys even if
  // i18n.language is still syncing (otherwise overrides land on the wrong locale bundle).
  const labelLang = getLangFromPathname(location.pathname)
  useEffect(() => {
    let cancelled = false
    fetchUiLabelsForLanguage(labelLang).then((labels) => {
      if (cancelled || typeof labels !== 'object' || labels === null) return
      const inst = i18nRef.current
      for (const [key, value] of Object.entries(labels)) {
        const v = value != null ? String(value).trim() : ''
        if (key && v !== '' && v !== key) {
          inst.addResource(labelLang, 'translation', key, v)
        }
      }
      // Defer so we never trigger subscriber setState during another component's render (React warning).
      const langAfter = inst.language
      queueMicrotask(() => {
        if (!cancelled) inst.emit('languageChanged', langAfter)
      })
    })
    return () => {
      cancelled = true
    }
  }, [labelLang, location.pathname])

  // Set HTML direction based on language on every route change
  // This ensures direction is correct even if language changes outside of LanguageSwitcher
  useEffect(() => {
    applyDirection(i18n.language)
  }, [i18n.language, location.pathname])

  // Dark mode toggle
  useEffect(() => {
    if (mode === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [mode])

  return (
    <Routes future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
        <Route path=":lang" element={<LangSync />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="quran" element={<QuranPage />} />
          <Route path="quran/index" element={<QuranNewIndexPage />} />
          <Route path="quran/listen" element={<QuranListenPage />} />
          <Route path="quran/:id" element={<QuranSurahPage />} />
          <Route path="islam-definition" element={<IslamDefinitionPage />} />
          <Route path="journey-to-islam" element={<JourneyToIslamPage />} />
          <Route path="doubt-responses" element={<DoubtResponsesPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/prayer" element={<PrayerTimesPage />} />
          <Route path="services/zakat" element={<ZakatCalculatorPage />} />
          <Route path="services/donations" element={<DonationsPage />} />
          <Route path="library" element={<LibraryIndexPage />} />
          <Route path="library/:id" element={<LibraryDetailPage />} />
          <Route path="library/book/:id" element={<LibraryBookPage />} />
          <Route path="videos" element={<VideosPage />} />
          <Route path="videos/:id" element={<VideoDetailPage />} />
          <Route path="section-card/:id" element={<SectionCardArticlePage />} />
          <Route path="payment/callback" element={<PaymentCallbackPage />} />
          <Route path="institutions" element={<InstitutionsPage />} />
          <Route path="reader" element={<ReaderPage />} />
          <Route path="references" element={<ReferencesPage />} />
        </Route>
      </Route>
    </Routes>
  )
}

export default App
